import { Mail, Phone } from "lucide-react";
import { Reveal, SectionTag } from "@/components/landing/Reveal";
import BookingForm from "@/components/landing/BookingForm";
import { CONTACTS } from "@/components/landing/SeoSchema";
import { useLang } from "@/lib/i18n";

export default function Booking({ packages, booking }) {
  const { t, dyn } = useLang();
  const tag = booking?.tag ? dyn(booking.tag) : t("booking.tag");
  const heading1 = booking?.heading1 ? dyn(booking.heading1) : t("booking.heading1");
  const headingAccent = booking?.headingAccent ? dyn(booking.headingAccent) : t("booking.headingAccent");
  const text = booking?.text ? dyn(booking.text) : t("booking.text");
  const chip1 = booking?.chip1 ? dyn(booking.chip1) : t("booking.chip1");
  const chip2 = booking?.chip2 ? dyn(booking.chip2) : t("booking.chip2");

  return (
    <section id="boka" data-testid="booking-section" className="relative overflow-hidden bg-ink py-24 sm:py-32">
      <div className="pointer-events-none absolute -right-40 top-0 h-[500px] w-[500px] rounded-full bg-forest/25 blur-[140px]" />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionTag number="09" label={tag} />
        <div className="grid gap-16 lg:grid-cols-2">
          <div>
            <Reveal>
              <h2 className="font-display text-3xl font-black leading-[1.08] tracking-tight text-bone sm:text-4xl lg:text-5xl">
                {heading1} <span className="text-mint">{headingAccent}</span>
              </h2>
            </Reveal>
            <Reveal delay={0.12}>
              <p className="mt-6 max-w-md text-base leading-relaxed text-fog sm:text-lg">
                {text}
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="mt-10 space-y-5">
                {CONTACTS.map((c) => (
                  <a
                    key={c.tel}
                    data-testid={`contact-phone-link-${c.testid}`}
                    href={`tel:${c.tel}`}
                    className="group flex items-center gap-4 text-bone transition-colors hover:text-mint"
                  >
                    <span className="flex h-12 w-12 items-center justify-center rounded-full border border-white/15 transition-colors group-hover:border-mint">
                      <Phone className="h-5 w-5" />
                    </span>
                    <span>
                      <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-fog">
                        {t("booking.call")} {c.name}
                      </span>
                      <span className="font-display text-lg font-bold">{c.phone}</span>
                    </span>
                  </a>
                ))}
                <a
                  data-testid="contact-email-link"
                  href="mailto:nivadigitals@gmail.com"
                  className="group flex items-center gap-4 text-bone transition-colors hover:text-mint"
                >
                  <span className="flex h-12 w-12 items-center justify-center rounded-full border border-white/15 transition-colors group-hover:border-mint">
                    <Mail className="h-5 w-5" />
                  </span>
                  <span>
                    <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-fog">{t("booking.email")}</span>
                    <span className="font-display text-lg font-bold">nivadigitals@gmail.com</span>
                  </span>
                </a>
              </div>
            </Reveal>
            <Reveal delay={0.28}>
              <div className="mt-10 flex flex-wrap gap-3">
                <span className="rounded-full border border-white/15 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-fog">
                  {chip1}
                </span>
                <span className="rounded-full border border-white/15 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-fog">
                  {chip2}
                </span>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.15}>
            <div className="rounded-2xl border border-white/10 bg-surface p-6 sm:p-10">
              <BookingForm packages={packages} />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
